import React, { useEffect } from 'react';
import { useLanguages } from '@/hooks/useLanguages';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';

const getPathWithoutLang = (pathname: string) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && parts[0].length === 2) {
    return '/' + parts.slice(1).join('/');
  }
  return pathname;
};

export const LanguageSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { data: languages } = useLanguages();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = i18n.language?.split('-')[0] || 'en';

  // Load user preference if available
  useEffect(() => {
    const loadPreference = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('user_preferences')
        .select('language_code')
        .eq('user_id', user.id)
        .maybeSingle();
      const pref = data?.language_code;
      if (pref && pref !== currentLang) {
        const path = getPathWithoutLang(location.pathname);
        i18n.changeLanguage(pref);
        navigate(`/${pref}${path}${location.search}`, { replace: true });
      }
    };
    void loadPreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onChange = async (code: string) => {
    const path = getPathWithoutLang(location.pathname);
    await i18n.changeLanguage(code);
    navigate(`/${code}${path}${location.search}`, { replace: true });
    if (user) {
      await supabase
        .from('user_preferences')
        .upsert({ user_id: user.id, language_code: code }, { onConflict: 'user_id' });
    }
  };

  const list = languages && languages.length > 0
    ? languages
    : [
        { code: 'en', name: 'English', native_name: 'English', dir: 'ltr', hreflang: 'en', is_default: true, is_active: true },
        { code: 'fr', name: 'French', native_name: 'Français', dir: 'ltr', hreflang: 'fr', is_default: false, is_active: true },
        { code: 'nl', name: 'Dutch', native_name: 'Nederlands', dir: 'ltr', hreflang: 'nl', is_default: false, is_active: true },
      ];

  return (
    <Select value={currentLang} onValueChange={onChange}>
      <SelectTrigger className="w-[150px]">
        <SelectValue placeholder={t('language.label')} />
      </SelectTrigger>
      <SelectContent>
        {list.map((l) => (
          <SelectItem key={l.code} value={l.code}>
            <span className="mr-2" aria-hidden>
              {l.code === 'en' ? '🇬🇧' : l.code === 'fr' ? '🇫🇷' : l.code === 'nl' ? '🇳🇱' : '🏳️'}
            </span>
            <span>{l.native_name}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSwitcher;